import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import debounce from "lodash.debounce";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { patientActions } from "../../../core/actions";
import {
  resolvePatientBillingLocationKey,
  getFilteredBillingLocations,
} from "./quotationLocationUtils";
import { fetchQuotationById } from "../ERPActions/erpActions";

/**
 * ConvertQuotationModal
 *
 * Allows the user to search and select an active patient, then converts
 * the selected quotation into a patient invoice using savePatientInvoice.
 *
 * API contracts (confirmed from backend source):
 *  - Patient list: getActiveVisitLists({ patientType: "InPatient" })
 *    → response.patient[]  (each has: id, fullName, erpCustId, visit.id, visit.type)
 *  - Save invoice: savePatientInvoice(locationId, invoicePayload, erpCustId)
 *    → HMgmtResponse { erpResp: { billNumber, ticketId } }
 *  - Quotation line items come from QuotationInfo.lines (List<QuotationLineInfo>)
 *    Fields: product (code), name, units, priceSell, price, discount, taxRate, taxId
 */
export default function ConvertQuotationModal({
  open,
  quotationData,       // full QuotationInfo row from the listing grid
  onClose,
  onSuccess,
  serverResposnes,     // shows message strip (matches existing prop name spelling)
  selectedLocationDetails,
  masterLocationDetails,
}) {
  const dispatch = useDispatch();
  const reduxMasterLocations = useSelector(
    (state) => state.erpDetailsState?.masterLocationDetails
  );
  const effectiveMasterLocations = masterLocationDetails || reduxMasterLocations;

  const dropdownRef = useRef(null);
  const patientInputRef = useRef(null);
  const [patientList, setPatientList] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [hideResults, setHideResults] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(() =>
    resolvePatientBillingLocationKey(null)
  );
  const [loading, setLoading] = useState(false);
  const [closedPatientWarning, setClosedPatientWarning] = useState("");
  const [quoteDetails, setQuoteDetails] = useState(quotationData);

  useEffect(() => {
    setQuoteDetails(quotationData);
    if (!open || !quotationData?.quoteId) return;
    if (!quotationData.lines || quotationData.lines.length === 0) {
      fetchQuotationById(quotationData.quoteId).then((fullQuote) => {
        if (fullQuote && fullQuote.lines && fullQuote.lines.length > 0) {
          setQuoteDetails((prev) => ({ ...prev, ...fullQuote }));
        }
      });
    }
  }, [open, quotationData]);

  // Auto focus select patient field whenever modal is opened
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (patientInputRef.current) {
          patientInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const effectiveQuote = quoteDetails || quotationData;
  const lines = useMemo(() => effectiveQuote?.lines || [], [effectiveQuote?.lines]);

  const grossSubtotal = useMemo(() => {
    return lines.reduce((acc, l) => {
      const units = Number(l.units || 1);
      const netUnit = l.price != null
        ? Number(l.price)
        : ((l.priceSell != null ? Number(l.priceSell) : 0) - (l.discount != null ? Number(l.discount) : 0));
      const gUnit = l.mrp != null
        ? Number(l.mrp)
        : (l.priceSell != null ? Number(l.priceSell) : (l.price != null ? Number(l.price) : 0));
      const uDisc = (l.discount != null && Number(l.discount) > 0)
        ? Number(l.discount)
        : Math.max(0, gUnit - netUnit);
      const effGross = gUnit > netUnit ? gUnit : (uDisc > 0 ? netUnit + uDisc : gUnit);
      return acc + (effGross * units);
    }, 0);
  }, [lines]);

  const totalDiscountAll = useMemo(() => {
    const linesDisc = lines.reduce((acc, l) => {
      const units = Number(l.units || 1);
      const gUnit = l.mrp != null
        ? Number(l.mrp)
        : (l.priceSell != null ? Number(l.priceSell) : (l.price != null ? Number(l.price) : 0));
      const netUnit = l.price != null
        ? Number(l.price)
        : ((l.priceSell != null ? Number(l.priceSell) : 0) - (l.discount != null ? Number(l.discount) : 0));
      // Use mrp - price (same as ViewERPQuotation) so both line-item AND overall discounts
      // are captured. l.discount only holds the per-line portion and misses the overall/header
      // discount (e.g. Facial: mrp=1000, price=810 → disc=190, not just l.discount=100).
      const uDisc = Math.max(0, gUnit - netUnit);
      return acc + (uDisc * units);
    }, 0);
    if (linesDisc > 0) return linesDisc;
    return parseFloat(effectiveQuote?.discountRs) || 0;
  }, [lines, effectiveQuote?.discountRs]);

  const roundOffVal = useMemo(() => {
    if (
      effectiveQuote?.roundOff != null &&
      effectiveQuote.roundOff !== "" &&
      !isNaN(parseFloat(effectiveQuote.roundOff))
    ) {
      return parseFloat(effectiveQuote.roundOff);
    }
    return 0;
  }, [effectiveQuote?.roundOff]);

  const payableAmt = useMemo(() => {
    const gross = Number(grossSubtotal.toFixed(2));
    const disc = Number(totalDiscountAll.toFixed(2));
    return Math.max(0, gross - disc + roundOffVal);
  }, [grossSubtotal, totalDiscountAll, roundOffVal]);

  useEffect(() => {
    const locKey = resolvePatientBillingLocationKey(selectedPatient);
    setSelectedLocation(locKey);
  }, [selectedPatient]);

  const billingLocationOptions = useMemo(
    () => getFilteredBillingLocations(effectiveMasterLocations),
    [effectiveMasterLocations]
  );

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setHideResults(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load patient when modal opens
  useEffect(() => {
    if (!open) return;
    // Reset state each time modal opens
    setSearch("");
    setSelectedPatient(null);
    setSelectedLocation(resolvePatientBillingLocationKey(null));
    setFilteredPatients([]);
    setHideResults(true);
    setSelectedIndex(-1);
    setClosedPatientWarning("");

    if (quotationData?.customerName || quotationData?.customerId) {
      const searchString = quotationData.customerName
        ? quotationData.customerName.trim()
        : String(quotationData.customerId);
      setSearch(searchString);
      // NOTE: Do NOT pass activeOnly: true here so we can detect if the customer is closed
      const sanitized = {
        bodyParams: { searchString, flag: "" },
        dateFrom: "",
        dateTo: "",
        page: 0,
        pageSize: 0,
      };

      dispatch(
        patientActions.patientSearch(sanitized, (resCb) => {
          if (resCb?.state === "success" && resCb.patientListCollection) {
            const mapped = resCb.patientListCollection.map((pat) => {
              let fullName = pat.fullName || "";
              if (pat.fname && pat.mname && pat.lname) fullName = `${pat.fname} ${pat.mname} ${pat.lname}`;
              else if (pat.fname && pat.lname) fullName = `${pat.fname} ${pat.lname}`;
              else if (pat.fname) fullName = pat.fname;

              return { ...pat, fullName, id: pat.id || pat.patientId || pat.personId };
            });
            if (mapped.length > 0) {
              const exactMatch = mapped.find(
                (p) =>
                  (quotationData.customerId != null &&
                    String(p.erpCustId) === String(quotationData.customerId)) ||
                  (quotationData.customerId != null &&
                    String(p.id) === String(quotationData.customerId))
              );
              if (exactMatch) {
                const isClosed =
                  !exactMatch.visit ||
                  !exactMatch.visit.status ||
                  exactMatch.visit.status.toLowerCase() === "close" ||
                  exactMatch.visit.internalStatus?.toLowerCase() === "forceclose";

                if (isClosed) {
                  const msg = "This patient is closed. Please onboard this patient to convert the quotation to invoice.";
                  setClosedPatientWarning(msg);
                  if (typeof serverResposnes === "function") {
                    serverResposnes(msg, "warning");
                  }
                  setSelectedPatient(null);
                  setSearch(`${exactMatch.id} - ${exactMatch.fullName} (Closed)`);
                  setFilteredPatients([]);
                  setHideResults(true);
                } else {
                  setSelectedPatient(exactMatch);
                  setSearch(`${exactMatch.id} - ${exactMatch.fullName}`);
                  setFilteredPatients([]);
                  setHideResults(true);
                  setClosedPatientWarning("");
                }
              } else {
                setFilteredPatients(mapped);
                setHideResults(false);
              }
            }
          }
        })
      );
    }
  }, [open, quotationData]);

  const debounceFetchPatients = useCallback(
    debounce((searchString) => {
      // NOTE: Pass activeOnly: true here for interactive active-patient search
      const sanitized = {
        bodyParams: { searchString, flag: "", activeOnly: true },
        activeOnly: true,
        dateFrom: "",
        dateTo: "",
        page: 0,
        pageSize: 0,
      };

      dispatch(
        patientActions.patientSearch(sanitized, (resCb) => {
          if (resCb?.state === "success" && resCb.patientListCollection) {
            const mapped = resCb.patientListCollection.map((pat) => {
              let fullName = pat.fullName || "";
              if (pat.fname && pat.mname && pat.lname) fullName = `${pat.fname} ${pat.mname} ${pat.lname}`;
              else if (pat.fname && pat.lname) fullName = `${pat.fname} ${pat.lname}`;
              else if (pat.fname) fullName = pat.fname;

              return { ...pat, fullName, id: pat.id || pat.patientId || pat.personId };
            });
            setFilteredPatients(mapped);
            setHideResults(mapped.length === 0);
          } else {
            setFilteredPatients([]);
            setHideResults(true);
          }
        })
      );
    }, 1000),
    [dispatch]
  );

  const handleConfirm = () => {
    if (closedPatientWarning) {
      if (typeof serverResposnes === "function") {
        serverResposnes(closedPatientWarning, "warning");
      }
      return;
    }
    if (!selectedPatient) {
      if (typeof serverResposnes === "function") {
        serverResposnes("Please select an active patient first.", "warning");
      }
      return;
    }
    if (!selectedPatient.visit?.id) {
      if (typeof serverResposnes === "function") {
        serverResposnes(
          "Selected patient does not have an active visit.",
          "warning"
        );
      }
      return;
    }
    if (!selectedLocation) {
      if (typeof serverResposnes === "function") {
        serverResposnes("Please select a billing location.", "warning");
      }
      return;
    }

    const patId = selectedPatient.id;
    const visId = selectedPatient.visit.id;
    const quoteId = quotationData?.quoteId || effectiveQuote?.quoteId;
    const locKey = selectedLocation;

    const targetUrl = `/billing/${patId}/${visId}?tab=3&fromQuote=${encodeURIComponent(
      quoteId
    )}&locKey=${encodeURIComponent(locKey)}`;

    if (typeof onClose === "function") {
      onClose();
    }
    window.location.href = targetUrl;
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearch(`${patient.id} - ${patient.fullName}`);
    setHideResults(true);
    setSelectedIndex(-1);
    setClosedPatientWarning("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onEntered: () => {
          if (patientInputRef.current) {
            patientInputRef.current.focus();
          }
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <i className="material-icons" style={{ color: "#4f46e5" }}>
            person_add
          </i>
          <span>Convert Quotation to Patient Invoice</span>
        </div>
        {(effectiveQuote?.billNumber || effectiveQuote?.quoteId) && (
          <div
            style={{
              fontSize: "12px",
              color: "#666",
              marginTop: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              Quotation: <strong>{effectiveQuote.billNumber || effectiveQuote.quoteId}</strong>
            </span>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>
              Total Payable:{" "}
              <span style={{ color: "#4f46e5", fontWeight: 700 }}>
                ₹ {payableAmt.toFixed(2)}
              </span>
            </span>
          </div>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <p style={{ fontSize: "13px", color: "#555", marginBottom: "14px" }}>
          Search and select an active inpatient, walkin patient, or outpatient, and select the billing location. All quotation items will be
          added as their invoice.
        </p>

        {closedPatientWarning && (
          <div
            style={{
              marginBottom: "14px",
              padding: "10px 14px",
              background: "#fff3cd",
              color: "#856404",
              borderRadius: "6px",
              border: "1px solid #ffeeba",
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i
              className="material-icons"
              style={{ fontSize: "18px", color: "#856404" }}
            >
              warning
            </i>
            <span>{closedPatientWarning}</span>
          </div>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled>
          <InputLabel id="billing-location-label">Billing Location</InputLabel>
          <Select
            labelId="billing-location-label"
            value={selectedLocation}
            label="Billing Location"
            disabled
          >
            {billingLocationOptions.map((loc) => (
              <MenuItem key={loc.locationKey} value={loc.locationKey}>
                {loc.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Patient Search — same pattern as AddHMSPaymentRequest */}
        <div style={{ position: "relative" }} ref={dropdownRef}>
          <TextField
            inputRef={patientInputRef}
            autoFocus
            label="Search Patient"
            size="small"
            fullWidth
            placeholder="Type patient name or ID..."
            value={search}
            type="search"
            autoComplete="off"
            onChange={(e) => {
              const val = e.target.value;
              setSearch(val);
              setSelectedIndex(-1);
              setSelectedPatient(null);
              setClosedPatientWarning("");

              if (val.length <= 2) {
                setFilteredPatients([]);
                setHideResults(true);
                return;
              }

              debounceFetchPatients(val);
            }}
            onKeyDown={(e) => {
              if (hideResults || filteredPatients.length === 0) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((prev) =>
                  prev < filteredPatients.length - 1 ? prev + 1 : 0
                );
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((prev) =>
                  prev > 0 ? prev - 1 : filteredPatients.length - 1
                );
              }
              if (e.key === "Enter" && selectedIndex >= 0) {
                handlePatientSelect(filteredPatients[selectedIndex]);
              }
            }}
          />

          {/* Dropdown list */}
          {!hideResults && filteredPatients.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "42px",
                left: 0,
                width: "100%",
                background: "white",
                zIndex: 9999,
                border: "1px solid #ddd",
                borderRadius: "4px",
                maxHeight: "220px",
                overflowY: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
              }}
            >
              {filteredPatients.map((p, idx) => {
                const phone =
                  p.phno || p.phone || p.phoneno || p.mobile || p.altPhno || "";
                const mainId = p.id || p.patientId || p.main || "";
                return (
                  <div
                    key={mainId || idx}
                    style={{
                      cursor: "pointer",
                      padding: "8px 12px",
                      background: idx === selectedIndex ? "#e8eaf6" : "white",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    onClick={() => handlePatientSelect(p)}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "3px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "13.5px",
                          color: "#1e293b",
                        }}
                      >
                        {p.fullName}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#475569",
                          fontWeight: 500,
                          backgroundColor: "#f1f5f9",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        MAIN: {mainId}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "11.5px",
                        color: "#64748b",
                      }}
                    >
                      <span>
                        {phone ? (
                          <span>📞 {phone}</span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>No contact number</span>
                        )}
                      </span>
                      {p.visit?.type && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#4f46e5",
                            fontWeight: 500,
                          }}
                        >
                          {p.visit.type}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected patient confirmation */}
        {selectedPatient && (
          <div
            style={{
              marginTop: "14px",
              padding: "10px 14px",
              background: "#f0f7ff",
              borderRadius: "6px",
              border: "1px solid #90caf9",
              fontSize: "13px",
            }}
          >
            <strong>Selected Patient:</strong> {selectedPatient.fullName}
            <span style={{ marginLeft: "12px", color: "#555" }}>
              MAIN: {selectedPatient.id}
            </span>
            {(selectedPatient.phno ||
              selectedPatient.phone ||
              selectedPatient.phoneno ||
              selectedPatient.mobile ||
              selectedPatient.altPhno) && (
              <span style={{ marginLeft: "12px", color: "#555" }}>
                Phone:{" "}
                {selectedPatient.phno ||
                  selectedPatient.phone ||
                  selectedPatient.phoneno ||
                  selectedPatient.mobile ||
                  selectedPatient.altPhno}
              </span>
            )}
            {selectedPatient.visit?.id && (
              <span style={{ marginLeft: "12px", color: "#555" }}>
                Visit: {selectedPatient.visit.id}
              </span>
            )}
          </div>
        )}

        {/* Quotation line items preview */}
        {lines.length > 0 && (
          <div style={{ marginTop: "14px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#444",
                }}
              >
                Quotation Items ({lines.length}):
              </span>
              {/* {totalDiscountAll > 0 && (
                <span
                  style={{
                    fontSize: "11px",
                    color: "#059669",
                    fontWeight: 600,
                    backgroundColor: "#ecfdf5",
                    padding: "1px 7px",
                    borderRadius: "4px",
                    border: "1px solid #a7f3d0",
                  }}
                >
                  Total Discount: ₹ {totalDiscountAll.toFixed(2)}
                </span>
              )} */}
            </div>
            <div
              style={{
                maxHeight: "160px",
                overflowY: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
              }}
            >
              {lines.map((line, idx) => {
                const units = Number(line.units || 1);
                const netUnit = line.price != null
                  ? Number(line.price)
                  : ((line.priceSell != null ? Number(line.priceSell) : 0) - (line.discount != null ? Number(line.discount) : 0));
                const gUnit = line.mrp != null
                  ? Number(line.mrp)
                  : (line.priceSell != null ? Number(line.priceSell) : (line.price != null ? Number(line.price) : 0));
                const unitDiscount = (line.discount != null && Number(line.discount) > 0)
                  ? Number(line.discount)
                  : Math.max(0, gUnit - netUnit);
                const effectiveGrossUnit = gUnit > netUnit ? gUnit : (unitDiscount > 0 ? netUnit + unitDiscount : gUnit);
                const totalLineDiscount = unitDiscount * units;
                const lineTotal = netUnit * units;

                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 10px",
                      fontSize: "12px",
                      borderBottom:
                        idx < lines.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                      background: idx % 2 === 0 ? "#fafafa" : "white",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        paddingRight: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          color: "#1e293b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={line.name || line.product}
                      >
                        {line.name || line.product}
                      </span>
                      {unitDiscount > 0 && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginTop: "2px",
                          }}
                        >
                          {/* <span
                            style={{
                              fontSize: "10.5px",
                              backgroundColor: "#ecfdf5",
                              color: "#059669",
                              border: "1px solid #a7f3d0",
                              borderRadius: "3px",
                              padding: "0.5px 5px",
                              fontWeight: 500,
                            }}
                          >
                            Disc: ₹ {unitDiscount.toFixed(2)}/unit
                            {units > 1 ? ` (-₹ ${totalLineDiscount.toFixed(2)})` : ""}
                          </span> */}
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ color: "#1e293b", fontWeight: 500 }}>
                        {units} × ₹ {netUnit.toFixed(2)}
                        {units > 1 && (
                          <span style={{ fontWeight: 600, marginLeft: "4px" }}>
                            = ₹ {lineTotal.toFixed(2)}
                          </span>
                        )}
                      </div>
                      {unitDiscount > 0 && effectiveGrossUnit > netUnit && (
                        <div
                          style={{
                            fontSize: "10.5px",
                            color: "#94a3b8",
                            textDecoration: "line-through",
                            marginTop: "1px",
                          }}
                        >
                          {units} × ₹ {effectiveGrossUnit.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals Summary */}
            <div
              style={{
                marginTop: "6px",
                padding: "8px 12px",
                background: "#f8fafc",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                fontSize: "12px",
              }}
            >
              {(totalDiscountAll > 0 || roundOffVal !== 0) && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                      color: "#64748b",
                    }}
                  >
                    <span>Total Bill Amount (Gross):</span>
                    <span>₹ {grossSubtotal.toFixed(2)}</span>
                  </div>
                  {totalDiscountAll > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "4px",
                        color: "#059669",
                        fontWeight: 500,
                      }}
                    >
                      <span>Total Discount:</span>
                      <span>- ₹ {totalDiscountAll.toFixed(2)}</span>
                    </div>
                  )}
                  {Boolean(roundOffVal !== 0) && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "4px",
                        color: "#64748b",
                      }}
                    >
                      <span>Round Off:</span>
                      <span>
                        {roundOffVal > 0
                          ? `+ ₹ ${roundOffVal.toFixed(2)}`
                          : `- ₹ ${Math.abs(roundOffVal).toFixed(2)}`}
                      </span>
                    </div>
                  )}
                </>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#1e293b",
                  borderTop:
                    (totalDiscountAll > 0 || roundOffVal !== 0)
                      ? "1px solid #e2e8f0"
                      : "none",
                  paddingTop:
                    (totalDiscountAll > 0 || roundOffVal !== 0) ? "5px" : "0",
                }}
              >
                <span>Total Payable Amount:</span>
                <span
                  style={{
                    color: "#4f46e5",
                    fontSize: "13.5px",
                    fontWeight: 700,
                  }}
                >
                  ₹ {(Math.max(0, Number(grossSubtotal.toFixed(2)) - Number(totalDiscountAll.toFixed(2)) + roundOffVal)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          startIcon={<Close />}
          color="error"
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedPatient || !selectedLocation || loading}
          sx={{
            backgroundColor: "#4f46e5",
            color: "#fff",
            "&:hover": { backgroundColor: "#4338ca" },
            minWidth: "160px",
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Confirm & Convert"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
